import { Flex, useScreenSize } from '@banhala/ui-kit';
import { ScrollView } from 'react-native-gesture-handler';
import type { FavoriteMarketWithNewGoods } from 'shared/src/api/favoriteMarketService/getFavoriteMarketsWithNewGoods/types';
import { MARKET_ITEM_WIDTH, MarketItem } from './MarketItem';
import { MarketMoreItem } from './MarketMoreItem';

const VISIBLE_LIMIT = 9;

interface MarketsSectionProps {
  markets: FavoriteMarketWithNewGoods[];
  onPressMarket: (market: FavoriteMarketWithNewGoods, index: number) => void;
  onPressMore: () => void;
}

export const MarketsSection = ({
  markets,
  onPressMarket,
  onPressMore,
}: MarketsSectionProps) => {
  if (markets.length === 0) return null;

  const visibleMarkets = markets.slice(0, VISIBLE_LIMIT);
  const hasMore = markets.length > VISIBLE_LIMIT;
  // 마켓 6개 초과 시 가로 스크롤 → 2줄 그리드 전환 (iOS와 동일)
  const isGridLayout = visibleMarkets.length > 6;

  return (
    <Flex>
      {isGridLayout ? (
        <MarketsGrid
          visibleMarkets={visibleMarkets}
          hasMore={hasMore}
          onPressMarket={onPressMarket}
          onPressMore={onPressMore}
        />
      ) : (
        <MarketsCarousel
          visibleMarkets={visibleMarkets}
          hasMore={hasMore}
          onPressMarket={onPressMarket}
          onPressMore={onPressMore}
        />
      )}
      <MarketsDivider />
    </Flex>
  );
};

interface MarketsLayoutProps {
  visibleMarkets: FavoriteMarketWithNewGoods[];
  hasMore: boolean;
  onPressMarket: (market: FavoriteMarketWithNewGoods, index: number) => void;
  onPressMore: () => void;
}

// 2행 × 5열 고정 그리드. 빈 슬롯은 자리만 차지해 SpaceBetween으로 균등 분배.
const MarketsGrid = ({
  visibleMarkets,
  hasMore,
  onPressMarket,
  onPressMore,
}: MarketsLayoutProps) => (
  <Flex padding={{ horizontal: 12, vertical: 20 }} gap={16}>
    {[0, 1].map(row => (
      <Flex key={row} horizontal justifyContent={'space-between'}>
        {[0, 1, 2, 3, 4].map(col => {
          const index = row * 5 + col;
          if (index === 9 && hasMore) {
            return <MarketMoreItem key={col} onPress={onPressMore} />;
          }
          const market = visibleMarkets[index];
          if (market == null) {
            return <Flex key={col} width={MARKET_ITEM_WIDTH} />;
          }
          return (
            <MarketItem
              key={col}
              market={market}
              index={index}
              onPress={() => onPressMarket(market, index)}
            />
          );
        })}
      </Flex>
    ))}
  </Flex>
);

const MarketsCarousel = ({
  visibleMarkets,
  hasMore,
  onPressMarket,
  onPressMore,
}: MarketsLayoutProps) => {
  const { width: screenWidth } = useScreenSize();
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={getCarouselContentStyle(screenWidth)}
    >
      {visibleMarkets.map((market, index) => (
        <MarketItem
          key={market.sno}
          market={market}
          index={index}
          onPress={() => onPressMarket(market, index)}
        />
      ))}
      {hasMore && <MarketMoreItem onPress={onPressMore} />}
    </ScrollView>
  );
};

const MarketsDivider = () => (
  <Flex>
    <Flex height={1} backgroundColor={'bg_tertiary'} />
    <Flex height={7} backgroundColor={'bg_secondary'} />
  </Flex>
);

// 한 줄에 5.5장이 보이도록 5개 간격을 계산 (iOS와 동일)
const getCarouselContentStyle = (screenWidth: number) => {
  const gap = Math.floor((screenWidth - 5.5 * MARKET_ITEM_WIDTH - 12) / 5);
  return {
    paddingHorizontal: 12,
    paddingVertical: 20,
    gap,
  };
};
